import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch } from "@/store/store";
import { syncFromURL } from "@/store/slices/userSelection/userSelection";
import { parseURLParams } from "@/utils/url";
import { buildContextKey } from "@/store/listeners/selectionListener";
import { updateScreenLevelSelection } from "@/store/slices/screenLevelSelection/screenLevelSelection";
import { selectSupportedURLKeys } from "@/store/slices/screenConfig/selectors";
import { AGE_END, AGE_START } from "@/constant/constantsThrougoutProject";

export const useURLToStore = () => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const supportedURLKeys = useSelector(selectSupportedURLKeys);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);

    // parseURLParams already uses getAll() for:
    //   brands, markets, segments, filters, secondaryBrands → number[]
    //   questionOptions → Record<string, number[]> (parsed from "qKey-optId" strings)
    // and get() for all other single-value keys → string
    const parsed: Record<string, any> = parseURLParams(searchParams);

    // Derive module from first pathname segment.
    // Fall back to "VISIBILITY" (not "EQUITY") so that an initial load at "/"
    // does not override the userSelection initial state with the wrong module
    // before Home.tsx has had a chance to redirect to the correct first module.
    const module =
      location.pathname.split("/")?.[1] === "emotional"
        ? "IMAGERY"
        : location.pathname.split("/")?.[1]?.toUpperCase() || "VISIBILITY";

    const screenType = location.pathname?.split("/")?.[2] || "brand";

    // Combine ageStart / ageEnd into ageRange: number[]
    // ageRange is a synthetic key — include it only when ageStart or ageEnd
    // is part of the supported URL keys for this screen.
    const ageSupported =
      !supportedURLKeys ||
      supportedURLKeys.includes(AGE_START) ||
      supportedURLKeys.includes(AGE_END);

    const ageStart = Number(parsed.ageStart);
    const ageEnd = Number(parsed.ageEnd);
    if (ageSupported) {
      if (!isNaN(ageStart) && ageStart > 0 && !isNaN(ageEnd) && ageEnd > 0) {
        parsed.ageRange = [ageStart, ageEnd];
      } else {
        parsed.ageRange = [];
      }
    }
    delete parsed.ageStart;
    delete parsed.ageEnd;

    // `parsed.filters` is now a structured `FilterSelection[]` produced by
    // the v2 `fv=2` + `f=` URL grammar (spec §6.5.1) — pass it through to
    // `syncFromURL` unmodified via the `...parsed` spread below. A legacy
    // numeric `filters=` param (no `fv=2`) or an absent `f=` param produces
    // no `filters` key at all (see `parseURLParams`), i.e. "the parser
    // produced nothing" — in that case, mirror the `segments` reset below:
    // filters are only a supported URL key on "brand" and "market" screen
    // types; for "segment" the key is not part of the URL schema so we must
    // NOT overwrite it.
    const filtersSupported = !supportedURLKeys || supportedURLKeys.includes("filters");
    if (
      filtersSupported &&
      (screenType === "brand" || screenType === "market") &&
      parsed.filters === undefined
    ) {
      parsed.filters = [];
    }

    const segmentsSupported = !supportedURLKeys || supportedURLKeys.includes("segments");
    if (segmentsSupported && !searchParams.has("segments")) {
      parsed.segments = [];
    }

    // Build the filtered payload for syncFromURL.
    // When supportedURLKeys is defined, strip out any parsed key that is not
    // declared as a supported URL param for this screen.  `module` and
    // `screenType` are derived (not URL params) and are always included.
    // `ageRange` is synthetic — its gating is already handled above.
    const filteredParsed: Record<string, any> =
      !supportedURLKeys
        ? { ...parsed }
        : Object.fromEntries(
            Object.entries(parsed).filter(
              ([key]) =>
                // Always allow synthetic/derived keys that are not raw URL params
                key === "ageRange" ||
                supportedURLKeys.includes(key),
            ),
          );

    dispatch(syncFromURL({ ...filteredParsed, module, screenType } as any));

    // ── Snapshot current screen selection → screenLevelSelection ────────────
    // Fired on every URL commit (including initial load and navigateTo calls
    // from selectionListener).  Saves the URL-derived brands/markets/segments/
    // secondaryBrands so that ModeDropdown / screen switches can restore them.
    // Only snapshot entity arrays that are declared as supported URL keys for
    // this screen to avoid overwriting valid state with empty arrays.
    const brandsSupported = !supportedURLKeys || supportedURLKeys.includes("brands");
    const marketsSupported = !supportedURLKeys || supportedURLKeys.includes("markets");
    const secondaryBrandsSupported = !supportedURLKeys || supportedURLKeys.includes("secondaryBrands");

    const contextKey = buildContextKey(
      screenType,
      (parsed.mode as string) ?? "POINT_IN_TIME",
      (parsed.visualizationType as string) ?? "",
    );
    dispatch(
      updateScreenLevelSelection({
        screenType: contextKey,
        value: {
          ...(brandsSupported && { brands: (filteredParsed.brands as number[]) ?? [] }),
          ...(marketsSupported && { markets: (filteredParsed.markets as number[]) ?? [] }),
          ...(segmentsSupported && { segments: (filteredParsed.segments as string[]) ?? [] }),
          ...(secondaryBrandsSupported && { secondaryBrands: (filteredParsed.secondaryBrands as number[]) ?? [] }),
        },
      }),
    );
  }, [location]);
};

export default useURLToStore;
